/**
 * Presentation helpers for analysis runs (M2 contract 9): selector item labels, version chips
 * and status chips. Pure functions over either the `metadata.run` block or a ledger RunDTO so
 * they can be unit tested without Vuetify.
 *
 * Both shapes are accepted: `metadata.run` uses `run_id`, `engine`, `config.version`,
 * `engines_hash`; the ledger document uses `_id`, `engine` / `engine_resolved`, `config`.
 */
import { formatAge } from '@/components/queue/queueFormat';
import { normalizeRunStatus } from '@/store/modules/analysisQueue';

const LEGACY_RUN_PREFIX = 'legacy-';
const HIGHLIGHTED_ENGINES = Object.freeze(['gestures', 'coherence']);
const INTERVIEW_ENGINES = Object.freeze([
  'beliefs',
  'competencies',
  'cultural_fit',
  'client_questions',
  'toolkit',
  'transcription',
]);

export const RUN_STATUS_PRESENTATION = Object.freeze({
  QUEUED: { label: 'Queued', color: 'grey', icon: 'mdi-clock-outline' },
  DISPATCHED: { label: 'Dispatched', color: 'primary', icon: 'mdi-send-clock-outline' },
  RUNNING: { label: 'Running', color: 'primary', icon: 'mdi-progress-clock' },
  CANCEL_REQUESTED: { label: 'Cancel requested', color: 'warning', icon: 'mdi-cancel' },
  SUCCEEDED: { label: 'Succeeded', color: 'success', icon: 'mdi-check-circle-outline' },
  FAILED: { label: 'Failed', color: 'error', icon: 'mdi-alert-circle-outline' },
  CANCELLED: { label: 'Cancelled', color: 'grey darken-2', icon: 'mdi-close-circle-outline' },
});

/** Run id from either shape (`run_id` on metadata.run, `_id` on ledger docs). */
export function runIdOf(run) {
  if (!run) return null;
  const id = run.run_id ?? run._id ?? null;
  return id == null ? null : String(id);
}

/** Legacy runs are backfilled ledger docs (`legacy-<doc id>`) or result docs without ledger. */
export function isLegacyRun(run) {
  if (!run) return true;
  const id = runIdOf(run);
  return id == null || id.startsWith(LEGACY_RUN_PREFIX);
}

export function runStatusPresentation(status) {
  const normalized = normalizeRunStatus(status);
  return (
    RUN_STATUS_PRESENTATION[normalized] || {
      label: status ? String(status) : 'Unknown',
      color: 'grey',
      icon: 'mdi-help-circle-outline',
    }
  );
}

export function shortHash(value, length = 8) {
  if (value == null || value === '') return '';
  return String(value).slice(0, length);
}

/** Engine block to display: resolved (finish) wins over the static one (start). */
export function engineBlockOf(run) {
  if (!run) return null;
  const block = run.engine_resolved || run.engine || null;
  return block && typeof block === 'object' ? block : null;
}

/** Config version number or null (metadata.run.config.version / ledger config.version). */
export function configVersionOf(run) {
  const version = run?.config?.version;
  const n = Number(version);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function enginesHashOf(run) {
  return run?.engines_hash ?? run?.config?.engines_hash ?? null;
}

/** 'config v3' when the version is known, else 'config <hash8>' or ''. */
export function configLabel(run) {
  const version = configVersionOf(run);
  if (version != null) return `config v${version}`;
  const hash = shortHash(enginesHashOf(run));
  return hash ? `config ${hash}` : '';
}

/** Instant that best represents when a run happened: finished, started, enqueued. */
export function runTimestamp(run) {
  return run?.finished_at || run?.started_at || run?.enqueued_at || null;
}

/**
 * Selector item label: 'Run #3 · threshold-60 · config v2 · 2 h ago'.
 * Missing parts are skipped; a run without attempt reads 'Run'.
 */
export function runLabel(run, { now = Date.now() } = {}) {
  if (!run) return '';
  const parts = [];
  const attempt = Number(run.attempt);
  parts.push(Number.isFinite(attempt) && attempt > 0 ? `Run #${attempt}` : 'Run');
  if (run.batch) parts.push(String(run.batch));
  const config = configLabel(run);
  if (config) parts.push(config);
  const age = formatAge(runTimestamp(run), now);
  if (age) parts.push(age);
  return parts.join(' · ');
}

/** Single-line header label: 'Run #3 · config v2 · gestures 2.0.3 / coherence 1.4.2 · build 707a6d5'. */
export function runHeadline(run) {
  if (!run) return '';
  const parts = [];
  const attempt = Number(run.attempt);
  parts.push(Number.isFinite(attempt) && attempt > 0 ? `Run #${attempt}` : 'Run');
  const config = configLabel(run);
  if (config) parts.push(config);
  const block = engineBlockOf(run);
  const engines = block?.engines && typeof block.engines === 'object' ? block.engines : {};
  const highlighted = HIGHLIGHTED_ENGINES.filter((name) => engines[name] != null).map(
    (name) => `${name} ${engines[name]}`
  );
  if (highlighted.length) parts.push(highlighted.join(' / '));
  if (block?.build_sha) parts.push(`build ${shortHash(block.build_sha, 7)}`);
  return parts.join(' · ');
}

function skippedEngineNames(run) {
  const skipped = Array.isArray(run?.engines_skipped) ? run.engines_skipped : [];
  return skipped
    .map((item) => (item && typeof item === 'object' ? item.engine || item.name : item))
    .filter(Boolean)
    .map(String);
}

/**
 * Version chips for RunVersionChips: 'Active' / 'Pinned', engine versions (gestures and
 * coherence always, interview engines only for interviews), 'config vN · <hash8>',
 * 'build <sha7>', 'partial' and 'degraded' with tooltips.
 *
 * @param {object|null} run metadata.run block or ledger document
 * @param {{ sourceType?: string }} opts
 * @returns {Array<{ key: string, text: string, color?: string, outlined?: boolean,
 *   mono?: boolean, icon?: string, tooltip?: string }>}
 */
export function runVersionChips(run, { sourceType = null } = {}) {
  if (!run) return [];
  const chips = [];
  if (run.is_active) {
    chips.push({
      key: 'active',
      text: 'Active',
      color: 'success',
      icon: 'mdi-star',
      tooltip: 'This run is the one the pages and metrics use for this video.',
    });
  }
  if (run.is_pinned) {
    chips.push({
      key: 'pinned',
      text: 'Pinned',
      color: 'info',
      icon: 'mdi-pin',
      tooltip: 'Pinned: new runs will not replace it until it is unpinned.',
    });
  }
  const block = engineBlockOf(run);
  const engines = block?.engines && typeof block.engines === 'object' ? block.engines : {};
  const isInterview = sourceType === 'interview';
  const shownEngines = [...HIGHLIGHTED_ENGINES, ...(isInterview ? INTERVIEW_ENGINES : [])].filter(
    (name) => engines[name] != null
  );
  shownEngines.forEach((name) => {
    chips.push({
      key: `engine-${name}`,
      text: `${name} ${engines[name]}`,
      outlined: true,
      tooltip: `${name} engine version ${engines[name]}`,
    });
  });
  const others = Object.keys(engines).filter((name) => !shownEngines.includes(name));
  if (others.length) {
    chips.push({
      key: 'engines-others',
      text: `+${others.length} engines`,
      outlined: true,
      tooltip: others.map((name) => `${name} ${engines[name]}`).join(' · '),
    });
  }
  const version = configVersionOf(run);
  const hash = shortHash(enginesHashOf(run));
  if (version != null || hash) {
    const text = [version != null ? `config v${version}` : 'config', hash]
      .filter(Boolean)
      .join(' · ');
    const fullHash = enginesHashOf(run);
    chips.push({
      key: 'config',
      text,
      outlined: true,
      mono: !!hash,
      tooltip: fullHash
        ? `Configuration snapshot hash ${fullHash}`
        : 'Configuration version used by this run',
    });
  }
  if (block?.build_sha) {
    const dev = block.build_sha === 'dev';
    chips.push({
      key: 'build',
      text: `build ${shortHash(block.build_sha, 7)}`,
      outlined: true,
      mono: true,
      color: dev ? 'warning' : undefined,
      tooltip: dev
        ? 'Build SHA unavailable (dev build): results are not reproducible by SHA.'
        : `Worker build ${block.build_sha}`,
    });
  }
  if (run.partial) {
    const skipped = skippedEngineNames(run);
    chips.push({
      key: 'partial',
      text: 'partial',
      color: 'warning',
      icon: 'mdi-alert-outline',
      tooltip: skipped.length
        ? `Some engines were skipped: ${skipped.join(', ')}`
        : 'Some engines did not run for this analysis.',
    });
  }
  if (block?.degraded) {
    const reasons = Array.isArray(block.degraded_reasons) ? block.degraded_reasons : [];
    const fallbacks = Array.isArray(block.silent_fallbacks) ? block.silent_fallbacks : [];
    const details = [
      ...reasons.map(String),
      ...fallbacks.map((item) =>
        item && typeof item === 'object'
          ? item.name || item.key || JSON.stringify(item)
          : String(item)
      ),
    ];
    chips.push({
      key: 'degraded',
      text: 'degraded',
      color: 'error',
      icon: 'mdi-alert-decagram-outline',
      tooltip: details.length
        ? `Degraded run: ${details.join(', ')}`
        : 'Degraded run: an engine fell back to a weaker model or default.',
    });
  }
  return chips;
}

const STATUS_ORDER = Object.freeze({
  SUCCEEDED: 0,
  RUNNING: 1,
  CANCEL_REQUESTED: 1,
  DISPATCHED: 2,
  QUEUED: 2,
  FAILED: 3,
  CANCELLED: 4,
});

function toMillis(value) {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

/** SUCCEEDED first (newest first inside each group), then in-flight, then failed/cancelled. */
export function sortRunsForSelector(runs) {
  const list = Array.isArray(runs) ? runs.filter(Boolean) : [];
  return [...list].sort((a, b) => {
    const orderA = STATUS_ORDER[normalizeRunStatus(a.status)] ?? 9;
    const orderB = STATUS_ORDER[normalizeRunStatus(b.status)] ?? 9;
    if (orderA !== orderB) return orderA - orderB;
    const timeDelta = toMillis(runTimestamp(b)) - toMillis(runTimestamp(a));
    if (timeDelta !== 0) return timeDelta;
    return (Number(b.attempt) || 0) - (Number(a.attempt) || 0);
  });
}
